// Speil av functions/stateShards.ts — registeret for state-shards.
//
// Deler av et foretaks state kan bo i egne dokumenter under
// `businesses/{id}/stateShards/{shardId}` i stedet for i `app/state`
// (docs/architecture/app-state-split-plan.md, steg 3-5). Et shard brukes bare for et
// foretak der rotfeltet `stateLayout` i app/state (utenfor `state`, skrevet bare av
// migreringsskriptet) sier `shards[shardId] = 'copied' | 'pruned'`. Uten markor er alt
// som for. Med 'copied' ligger den gamle kopien fortsatt i app/state; den skal ingen
// rore, for `restore` trenger den. Shardet er sannheten.
//
// Over lasting og lagring ser sidene i src/ ett samlet `appState`, slik de gjor for
// personaldata (personnelState.js).
//
// REGISTERET MAA HOLDES I SYNK med functions/stateShards.ts. stateShards.test.js
// sammenligner funksjonene med backend-versjonen.

(function () {
  const STATE_SHARDS = {
    receipts: ['receiptInbox'],
    investments: ['investmentRegister'],
    accountingImport: ['accountingImportReportStatsByYear'],
    bank: ['bankStatements', 'bankMatchGroups', 'bankReconciliationPostings'],
    counters: ['accountingRecordedSeqCounter', 'accountingSourceVersions'],
  };

  // Shards klienten kan skrive direkte. Resten baerer server-eide felt og skrives bare av
  // Functions; firestore.rules nekter klientskriving dit, saa en spuriøs diff her ville
  // ellers feilet hele lagringen.
  const CLIENT_WRITABLE_SHARDS = ['receipts'];

  const STATE_LAYOUT_VERSION = 1;
  const ACTIVE_STATUSES = new Set(['copied', 'pruned']);

  const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

  const getStateShardPath = (businessId, shardId) => `businesses/${businessId}/stateShards/${shardId}`;

  // Shards som er autoritative for foretaket, ut fra rotdataene i app/state.
  const activeStateShards = (appDocData) => {
    const shards = isPlainObject(appDocData?.stateLayout?.shards) ? appDocData.stateLayout.shards : {};
    return Object.keys(STATE_SHARDS).filter((shardId) => ACTIVE_STATUSES.has(shards[shardId]));
  };

  const stateShardStatus = (appDocData, shardId) => {
    const status = appDocData?.stateLayout?.shards?.[shardId];
    return typeof status === 'string' ? status : null;
  };

  // Shardet vinner for sine felt: mangler feltet i shardet, mangler det i resultatet,
  // selv om app/state fortsatt har en gammel kopi.
  const mergeStateShards = (appState, shardStates, activeShardIds) => {
    const result = isPlainObject(appState) ? { ...appState } : {};
    for (const shardId of activeShardIds) {
      const shard = isPlainObject(shardStates?.[shardId]) ? shardStates[shardId] : {};
      for (const field of STATE_SHARDS[shardId] || []) {
        if (field in shard) result[field] = shard[field];
        else delete result[field];
      }
    }
    return result;
  };

  // Deler et samlet state for lagring. Felt som mangler i `state` skrives ikke.
  const splitStateShards = (state, activeShardIds) => {
    const appState = isPlainObject(state) ? { ...state } : {};
    const shardStates = {};
    for (const shardId of activeShardIds) {
      const picked = {};
      for (const field of STATE_SHARDS[shardId] || []) {
        if (field in appState) {
          picked[field] = appState[field];
          delete appState[field];
        }
      }
      shardStates[shardId] = picked;
    }
    return { appState, shardStates };
  };

  // Den gamle kopien et 'copied' shard etterlater i app/state.
  const legacyShardFields = (appState, appDocData) => {
    const legacy = {};
    for (const shardId of activeStateShards(appDocData)) {
      if (stateShardStatus(appDocData, shardId) !== 'copied') continue;
      for (const field of STATE_SHARDS[shardId] || []) {
        if (isPlainObject(appState) && field in appState) legacy[field] = appState[field];
      }
    }
    return legacy;
  };

  // ---------------------------------------------------------------------------
  // Bare i klienten: lytting og lagring (brukes av backend-state-access.jsx).

  const revisionOf = (data) => {
    const updatedAt = data?.updatedAt;
    return updatedAt?.toDate ? updatedAt.toDate().toISOString() : String(updatedAt || '');
  };

  const omitStateShardFields = (state, activeShardIds) => splitStateShards(state, activeShardIds).appState;

  // Lytter pa shard-dokumentene app/state-markoren peker pa. `setLayout` kalles med
  // rotdataene ved hvert app/state-oyeblikksbilde, sa en migrering eller `restore`
  // som skjer mens fanen er apen kobler lyttere til og fra underveis. Til alle aktive
  // shards har levert sitt forste oyeblikksbilde er `ready()` false: et samlet state
  // uten shardet ville fatt `receiptInbox: []` fra mergeState, og neste lagring kunne
  // da tomt kvitteringene.
  const createStateShardSubscription = ({ businessRef, isCurrent, onChange, onError }) => {
    let active = [];
    const listeners = new Map();
    const states = {};
    const revisions = {};
    const arrived = new Set();

    const detach = (shardId) => {
      listeners.get(shardId)?.();
      listeners.delete(shardId);
      arrived.delete(shardId);
      delete states[shardId];
      delete revisions[shardId];
    };

    const attach = (shardId) => {
      const unsubscribe = businessRef.collection('stateShards').doc(shardId).onSnapshot(
        (snapshot) => {
          if (!isCurrent() || !listeners.has(shardId)) return;
          const data = snapshot.exists ? snapshot.data() || {} : {};
          states[shardId] = isPlainObject(data.state) ? data.state : {};
          revisions[shardId] = snapshot.exists ? revisionOf(data) : '';
          arrived.add(shardId);
          onChange();
        },
        (error) => {
          if (!isCurrent() || !listeners.has(shardId)) return;
          onError(error);
        },
      );
      listeners.set(shardId, unsubscribe);
    };

    return {
      setLayout(appDocData) {
        const next = activeStateShards(appDocData);
        active.filter((shardId) => !next.includes(shardId)).forEach(detach);
        active = next;
        next.filter((shardId) => !listeners.has(shardId)).forEach(attach);
      },
      ready: () => active.every((shardId) => arrived.has(shardId)),
      // Uten aktive shards returneres app/state-objektet urort.
      merge: (appState) => (active.length ? mergeStateShards(appState, states, active) : appState),
      revisions: () => Object.fromEntries(active.map((shardId) => [shardId, revisions[shardId] || ''])),
      stop() {
        [...listeners.keys()].forEach(detach);
        active = [];
      },
    };
  };

  // Lagring for et migrert foretak. Leser shard-dokumentene i samme transaksjon som
  // app/state, og planlegger skriving bare for felt som faktisk er endret mot shardet.
  // `fieldSignature(state, fields)` gir en stabil streng for de oppgitte feltene (samme
  // normalisering som app/state-sjekken). Samme konfliktregel som app/state: ulik
  // revisjon er greit bare nar `expectedState` viser at klientens utgangspunkt stemmer
  // med det som ligger i shardet na. `staleState()` lager feilen som kastes da.
  const planStateShardWrites = async ({ transaction, businessRef, activeShardIds, state, expectedState, expectedRevisions, fieldSignature, staleState }) => {
    const writableShardIds = activeShardIds.filter((shardId) => CLIENT_WRITABLE_SHARDS.includes(shardId));
    const refs = writableShardIds.map((shardId) => businessRef.collection('stateShards').doc(shardId));
    const snapshots = await Promise.all(refs.map((ref) => transaction.get(ref)));
    const { shardStates } = splitStateShards(state, writableShardIds);
    const writes = [];
    writableShardIds.forEach((shardId, index) => {
      const data = snapshots[index].exists ? snapshots[index].data() || {} : {};
      const current = isPlainObject(data.state) ? data.state : {};
      const fields = Object.keys(shardStates[shardId]).filter((field) =>
        fieldSignature(shardStates[shardId], [field]) !== fieldSignature(current, [field]));
      if (!fields.length) return;
      const expectedRevision = expectedRevisions?.[shardId] || '';
      const currentRevision = snapshots[index].exists ? revisionOf(data) : '';
      if (expectedRevision && currentRevision && expectedRevision !== currentRevision) {
        if (!expectedState || fieldSignature(expectedState, fields) !== fieldSignature(current, fields)) throw staleState();
      }
      writes.push({ ref: refs[index], fields, state: Object.fromEntries(fields.map((field) => [field, shardStates[shardId][field]])) });
    });
    return writes;
  };

  window.PlummeStateShards = {
    STATE_SHARDS,
    CLIENT_WRITABLE_SHARDS,
    STATE_LAYOUT_VERSION,
    getStateShardPath,
    activeStateShards,
    stateShardStatus,
    mergeStateShards,
    splitStateShards,
    legacyShardFields,
    omitStateShardFields,
    createStateShardSubscription,
    planStateShardWrites,
  };
})();
